import {HistoryMaterialStorage} from '@domain/material-storage'
import {type ScheduleItem} from '@domain/schedule'
import {dataTestId} from '@hconnect/uikit'
import {Stack, Typography} from '@mui/material'
import {useHistoryStoragesByIdQuery} from '@widgets/planning-schedule-chart'
import {Moment} from 'moment-timezone'
import {FC} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../../../consts'

import {SelectStorageButton} from './SelectStorageButton'

interface SelectStoragesProps {
  selectedStorages: (Pick<HistoryMaterialStorage, 'id' | 'name'> & {isSelected: boolean})[]
  scheduleItem: ScheduleItem
  startOfPlan: Moment
  endOfPlan: Moment
  isReadOnly: boolean
  updateScheduleItem?: (item: ScheduleItem) => void
  isUpdateScheduleItemLoading?: boolean
}

export const SelectStorages: FC<SelectStoragesProps> = ({
  selectedStorages,
  scheduleItem,
  startOfPlan,
  endOfPlan,
  isReadOnly,
  updateScheduleItem,
  isUpdateScheduleItemLoading = false
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const {data: storagesById} = useHistoryStoragesByIdQuery({
    timeFrame: [startOfPlan, endOfPlan]
  })
  if (!storagesById) {
    throw new Error('BUG: storagesById should be loaded before rendering this component')
  }
  const {
    selectedSilos: {ids: allSelectedSiloIds}
  } = scheduleItem

  // BE stores selected storages in scheduler without any storage knowledge, we need to check it ourselves
  const isSelectedStorageIdValid = (storageId: number) =>
    selectedStorages.map(({id}) => id).includes(storageId)

  const selectedSilosIds = allSelectedSiloIds.filter(isSelectedStorageIdValid)

  const getNewStoragesIdsSorted = (id: number) => {
    return selectedSilosIds.includes(id)
      ? selectedSilosIds.filter((selectedId) => selectedId !== id)
      : [...selectedSilosIds, id].sort((a, b) =>
          storagesById[a].name.localeCompare(storagesById[b].name)
        )
  }

  const handleSelectStorage = (storageId: number) => {
    const updatedIds = getNewStoragesIdsSorted(storageId)
    updateScheduleItem?.({
      ...scheduleItem,
      selectedSilos: {...scheduleItem.selectedSilos, ids: updatedIds}
    })
  }
  const isSelectStorageButtonDisabled = isReadOnly || isUpdateScheduleItemLoading

  return (
    <Stack spacing={2} {...dataTestId('unordered_storages_selector')}>
      <Typography variant="body2">{t('planning.deselectSilosInfo')}</Typography>
      <Stack direction="row" flexWrap="wrap" gap={1.5}>
        {selectedStorages.map((storage) => (
          <SelectStorageButton
            key={storage.id}
            storage={storage}
            isSelected={storage.isSelected}
            isDisabled={isSelectStorageButtonDisabled}
            handleSelectStorage={handleSelectStorage}
          />
        ))}
      </Stack>
    </Stack>
  )
}
